import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FileUploadModel } from '@fi-app/utilities/file-upload/file-upload.model';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { IMediaBankFile, IMediaBankFileReference } from '@fi-shared/media-bank';
import { FileDropDirective } from '@fi-shared/fi-directives/file-drop.directive';
import { FileUploadComponent } from '@fi-app/utilities/file-upload/file-upload.component';
import { FiUploadType } from '@fi-shared/types/upload-type';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiImageSize, FileType } from '@fi-shared/types';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { IInsertMediaUploadItem } from '@fi-shared/fi-insert-media/fi-insert-media';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';

@Component({
    selector: 'fi-customer-settings-edit-image',
    templateUrl: './edit-image.component.html',
    styleUrls: ['./edit-image.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-customer-settings-edit-image' },
    standalone: false,
})
export class FiCustomerSettingsEditImageComponent implements OnInit {
    @Input() fileReference: IMediaBankFileReference;
    @Output() fileReferenceChange = new EventEmitter();
    @Input() recommendedSize: string;
    @Input() uploadType = FiUploadType.Common;
    @Input() imageSize: FiImageSize;
    @ViewChild('fiFileDrop', { static: false }) fiFileDrop: FileDropDirective;
    @ViewChild(FileUploadComponent, { static: false }) fileUploadComponent: FileUploadComponent;

    fileUploadModel: FileUploadModel;
    fileUploadAcceptExtensions: string = '.jpg,.jpeg,.gif,.png';

    constructor(
        private loadingService: LoadingService,
        private fiDialogsService: FiDialogsService,
        private mediaStorageService: FiMediaStorageService,
        private mediaBankHelperService: MediaBankHelperService,
        private fileUploadService: FiFileUpload2Service,
    ) {}

    ngOnInit(): void {
        this.setFileUploadModel();
    }

    setFileUploadModel() {
        this.fileUploadModel = new FileUploadModel(($files: FileList) => this.onFileSelect($files));
        this.fileUploadModel.SelectFilesTextId = 'common.choose_image';
        this.fileUploadModel.DropFilesTextId = 'common.dnd_image';
        this.fileUploadModel.accept = this.fileUploadAcceptExtensions;
        this.fileUploadModel.fileTypes = [FileType.Image];
        //this.fileUploadModel.SkipToggleLoadingOnProcessFiles = true;
        //this.FileUploadModel.acceptValidate = ($file) => this.validateImage($file);
    }

    onFileSelect($files: FileList) {
        const acceptExt: string[] = this.fileUploadModel.accept.split(',');
        const extRegexp: RegExp = /(\.[^\.]+)$/;
        const acceptedFiles = Array.from($files).filter((file: File) => {
            const match: RegExpMatchArray = file.name.match(extRegexp);
            return match && acceptExt.indexOf(match[1]) !== -1;
        });

        if (!acceptedFiles.length) {
            return;
        }

        let uploadedMediaBankFile: IMediaBankFile;
        this.loadingService
            .trackPromise(
                this.fileUploadService.uploadFiles({
                    files: [acceptedFiles[0]],
                    onUploadStarted: (uploadItem: IInsertMediaUploadItem) =>
                        (uploadedMediaBankFile = uploadItem.mediaBankFile),
                    skipSaveMediaBankOnBackend: true,
                    formData: {
                        uploadType: this.uploadType,
                    },
                }),
            )
            .then(() => {
                const fileReference = this.mediaBankHelperService.getMediaBankFileReferenceFromMediaFile(
                    uploadedMediaBankFile,
                    {
                        isNewUpload: true,
                        showInMediaBank: false,
                    },
                );
                this.setFileReference(fileReference);
            });
    }

    getImageUrl(): string {
        if (this.fileReference) {
            return this.mediaStorageService.getMediaFileUrl(this.fileReference, { size: this.imageSize });
        }
    }

    removeImage() {
        this.setFileReference(null);
    }

    setFileReference(fileReference: IMediaBankFileReference) {
        this.fileReference = fileReference;
        this.fileReferenceChange.emit(this.fileReference);
    }
}
